import { useEffect, useState } from "react";
import { applyMapEvent, parseSnapshot, type MapSnapshot } from "./live-map-data";

export type MapConnection = "connecting" | "live" | "reconnecting" | "paused" | "offline" | "denied";
export function useLiveMap(paused: boolean) {
  const [data, setData] = useState<MapSnapshot | null>(null);
  const [connection, setConnection] = useState<MapConnection>("connecting");
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [visible, setVisible] = useState(document.visibilityState !== "hidden");
  useEffect(() => {
    const changed = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", changed);
    return () => document.removeEventListener("visibilitychange", changed);
  }, []);
  useEffect(() => {
    if (paused || !visible) { setConnection("paused"); return; }
    let disposed = false, source: EventSource | null = null, timer: ReturnType<typeof setTimeout> | undefined, failures = 0, generation = 0;
    const controller = new AbortController();
    const retry = (message: string) => {
      if (disposed) return;
      source?.close(); source = null;
      setError(message);
      setConnection(navigator.onLine ? "reconnecting" : "offline");
      clearTimeout(timer);
      timer = setTimeout(() => { void connect(); }, Math.min(30000, 2000 * 2 ** Math.min(failures++, 4)));
    };
    const connect = async () => {
      const requestGeneration = ++generation;
      source?.close(); source = null;
      if (!navigator.onLine) { setConnection("offline"); setError("Network offline. Displaying the last received state."); return; }
      try {
        setConnection(failures ? "reconnecting" : "connecting");
        const response = await fetch("/api/live-map/snapshot", { credentials: "same-origin", cache: "no-store", signal: controller.signal });
        if (disposed || requestGeneration !== generation) return;
        if (response.status === 401 || response.status === 403) {
          setData(null); setUpdatedAt(null); setConnection("denied"); setError("Sign in with an account that has access to the Live Map."); return;
        }
        if (!response.ok) throw new Error("The live feed is unavailable. Retrying automatically.");
        let latest = parseSnapshot(await response.json());
        if (disposed || requestGeneration !== generation) return;
        setData(latest); setUpdatedAt(new Date().toISOString()); setError("");
        source = new EventSource(`/api/live-map/events?cursor=${encodeURIComponent(latest.cursor)}`);
        source.onopen = () => { if (!disposed && requestGeneration === generation) { failures = 0; setConnection("live"); setError(""); } };
        source.onerror = () => { if (requestGeneration === generation) retry("Connection interrupted. Displaying the last received state."); };
        for (const type of ["snapshot", "reset", "activity", "streams", "status"]) source.addEventListener(type, (event) => {
          if (disposed || requestGeneration !== generation) return;
          try {
            const payload: unknown = JSON.parse((event as MessageEvent<string>).data);
            latest = applyMapEvent(latest, type, payload);
            setData(latest);
            setUpdatedAt(new Date().toISOString());
          } catch { retry("The live feed sent an unreadable update. Reconnecting."); }
        });
      } catch (cause) {
        if (!disposed && requestGeneration === generation) retry(cause instanceof Error ? cause.message : "Unable to connect to the live feed.");
      }
    };
    const online = () => { clearTimeout(timer); source?.close(); void connect(); };
    const offline = () => {
      generation++;
      clearTimeout(timer); source?.close(); source = null;
      setConnection("offline"); setError("Network offline. Displaying the last received state.");
    };
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    void connect();
    return () => { disposed = true; controller.abort(); source?.close(); clearTimeout(timer); window.removeEventListener("online", online); window.removeEventListener("offline", offline); };
  }, [paused, visible, attempt]);
  return { data, connection, error, updatedAt, retry: () => setAttempt((value) => value + 1) };
}
